import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import {
  Application,
  APPLICATION_STATUS,
  CreateApplication,
  Enrollment,
  FindManyApplications,
  FindOne,
  Offer,
  PaginateResult,
  Timeline,
  UpdateApplication,
} from '@univacity/shared/types';
import { ObjectUtil } from '@univacity/shared/util';
import { Observable } from 'rxjs';
import { Configuration } from '../config/configuration';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService {
  private readonly baseUrl: string;

  constructor(private http: HttpClient, private config: Configuration) {
    this.baseUrl = config.apiMainUrl + '/application';
  }

  create(body: CreateApplication): Observable<Application> {
    return this.http.post<Application>(
      this.baseUrl,
      ObjectUtil.removeEmptyProperties(body)
    );
  }

  getOverviewStats(): Observable<Record<APPLICATION_STATUS, number>> {
    return this.http.get<Record<APPLICATION_STATUS, number>>(
      `${this.baseUrl}/overview/stats`
    );
  }

  /**
   * @description fetch timeline array for a student
   */
  getApplicationTimeline(
    id: string,
    page = 1
  ): Observable<PaginateResult<Timeline>> {
    return this.http.get<PaginateResult<Timeline>>(
      this.baseUrl + `/student/timeline`,
      {
        params: {
          application: id,
          page,
        },
      }
    );
  }

  getApplicationsByEmail(
    email: string,
    page = 1,
    status = ''
  ): Observable<PaginateResult<Application>> {
    return this.http.get<PaginateResult<Application>>(this.baseUrl, {
      params: {
        email,
        page,
        status: status ? (status === 'ALL' ? [] : status) : [],
      },
    });
  }

  find(
    queryParams: FindManyApplications = {}
  ): Observable<PaginateResult<Application>> {
    const params = new HttpParams({
      fromObject: ObjectUtil.removeEmptyProperties(queryParams) as Params,
    });

    return this.http.get<PaginateResult<Application>>(this.baseUrl, { params });
  }
  count(queryParams?: FindManyApplications): Observable<number> {
    const params = new HttpParams({
      fromObject: queryParams as Params,
    });

    return this.http.get<number>(`${this.baseUrl}/count`, {
      params,
    });
  }

  findOne(id: string, query: FindOne = {}): Observable<Application> {
    const params = new HttpParams({ fromObject: query as Params });
    return this.http.get<Application>(`${this.baseUrl}/${id}`, {
      params,
    });
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  update(id: string, application: UpdateApplication): Observable<Application> {
    return this.http.put<Application>(`${this.baseUrl}/${id}`, application);
  }

  updateStatus(
    id: string,
    status: APPLICATION_STATUS
  ): Observable<Application> {
    return this.http.patch<Application>(`${this.baseUrl}/${id}/status`, {
      status: status,
    });
  }

  submit(id: string, application?: UpdateApplication): Observable<Application> {
    return this.http.patch<Application>(
      `${this.baseUrl}/${id}/submit`,
      application
    );
  }

  accept(id: string): Observable<Application> {
    return this.http.get<Application>(`${this.baseUrl}/${id}/accept`);
  }

  admit(id: string, request: Offer): Observable<Application> {
    return this.http.patch<Application>(`${this.baseUrl}/${id}/admit`, request);
  }

  enroll(id: string, request: Enrollment): Observable<Application> {
    return this.http.patch<Application>(
      `${this.baseUrl}/${id}/enroll`,
      request
    );
  }

  reject(id: string, request: Offer): Observable<Application> {
    return this.http.patch<Application>(
      `${this.baseUrl}/${id}/reject`,
      request
    );
  }

  cancel(id: string, request?: Offer): Observable<Application> {
    return this.http.patch<Application>(
      `${this.baseUrl}/${id}/cancel`,
      request
    );
  }
}
